import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Card, Form, Input, Button } from '../../core/styles/styledComponents';
import styled from 'styled-components';
import { theme } from '../../core/styles/theme';
import { useAuth } from '../../context/AuthContext';

const Logo = styled.img`
  height: 50px;
  margin: ${theme.spacing.lg} 0;
`;

const LoginContainer = styled(Container)`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${theme.colors.background.default};
`;

const LoginCard = styled(Card)`
  width: 100%;
  max-width: 400px;
  margin-top: ${theme.spacing.lg};
`;

const RememberMeContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
`;

const Checkbox = styled.input`
  margin-right: ${theme.spacing.sm};
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.body.fontSize};
  cursor: pointer;
`;

const PasswordContainer = styled.div`
  position: relative;
  width: 100%;
`;

const PasswordInput = styled(Input)`
  padding-right: 100px;
`;

const TogglePasswordButton = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  font-size: ${theme.typography.body.fontSize};
  padding: 5px;
  &:hover {
    color: ${theme.colors.primary.main};
  }
`;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, isLoading, isAuthenticated, getAuthRedirectUrl } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await login(formData.email, formData.password);

      // If remember me is checked, store the email in localStorage
      if (formData.rememberMe) {
        localStorage.setItem('rememberedEmail', formData.email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }

      // On successful login, redirect to auth bridge URL
      const redirectUrl = getAuthRedirectUrl();
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        // This should never happen since we throw an exception if no token is found
        throw new Error('No redirect URL available after successful login');
      }
    } catch (error) {
      // Error is already handled by the AuthContext
      console.error('Login failed:', error);
    }
  };

  // Check for remembered email on component mount and redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      const redirectUrl = getAuthRedirectUrl();
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        // This should never happen since we throw an exception if no token is found
        throw new Error('No redirect URL available for authenticated user');
      }
      return;
    }

    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setFormData(prev => ({
        ...prev,
        email: rememberedEmail,
        rememberMe: true,
      }));
    }
  }, [isAuthenticated, navigate, getAuthRedirectUrl]);

  return (
    <>
      <LoginContainer>
        <Logo src="/EureekaBi.png" alt="EureekaBI Logo" />
        <LoginCard>
          <h2 style={{ marginBottom: theme.spacing.lg }}>Welcome back to EureekaBI</h2>
          <Form onSubmit={handleSubmit}>
            <Input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
            <PasswordContainer>
              <PasswordInput
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                required
                disabled={isLoading}
              />
              <TogglePasswordButton
                type="button"
                onClick={togglePasswordVisibility}
                disabled={isLoading}
              >
                {showPassword ? "Hide" : "Show"}
              </TogglePasswordButton>
            </PasswordContainer>
            <RememberMeContainer>
              <Checkbox
                type="checkbox"
                id="rememberMe"
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleChange}
                disabled={isLoading}
              />
              <CheckboxLabel htmlFor="rememberMe">Remember me</CheckboxLabel>
            </RememberMeContainer>
            <Button type="submit" variant="primary" disabled={isLoading}>
              {isLoading ? 'Signing In...' : 'Sign In'}
            </Button>
            <Button type="button" variant="minimal" onClick={() => navigate('/forgot-password')} disabled={isLoading}>
              Forgot Password?
            </Button>
            <Button type="button" variant="minimal" onClick={() => navigate('/register')} disabled={isLoading}>
              Don't have an account? Sign Up
            </Button>
          </Form>
        </LoginCard>
      </LoginContainer>
    </>
  );
};

export default LoginPage; 